import { digger } from '@cbre/digger';
import { abbrevNumber } from '@miq/utils';
// L2-S6: `isGaodeGeocode` is imported (not just re-exported) because `multipleLocations` and
// `singleLocation` below still call it. `MapClientKey` left with it — it was only ever used by the
// two predicates that moved.
// L2-S12: `buildPinSpecs` is the leaflet-free core of `buildMarkers` (see that function).
import { buildPinSpecs, isGaodeGeocode } from '@miq/shared-models';
import { LOCALE_MAP_CLIENT_MAP, LocaleMapId } from '@maps/maps.models';
import { DataObject } from '@shared/models/data-object.model';
import { EntityType } from '@shared/models/entity-enum';
// L2-S12: `ADD` left with `buildMarkers`' pin logic (the `value === ADD` temp-pin branch is now in
// @miq/shared-models maps/pin-spec.ts); `EMPTY_STRING` still has callers below.
import { EMPTY_STRING } from '@shared/models/strings.constants';
import '@maps/leaflet-global'; // must precede any leaflet plugin import
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { HtmlAttrsNames, HtmlTagNames } from './card-info/html-elements';
import {
  EntityInfoColloquialModel,
  GeoPolygon,
  GeoPolygonType,
  LatLong,
  LeafletPosition,
  LeafletShape,
  MetricConversion,
} from './esri-map.model';
// L2-S12: `PinPadding` left with `getPaddingValue` (both now in @miq/shared-models maps/pin-spec.ts
// / maps/map-icon.enum.ts). `svgIconFactory` is the data-URL + `L.icon` wrapper `buildMarkers` needs
// now that it is handed a finished pin string.
import { MapIcon, svgIconFactory, svgIconMaker } from './icon-markers.service';

/** SERVICES  */
export const getViewPortPolygon = bounds => {
  const { _northEast, _southWest } = bounds;
  const { lat: northLat, lng: eastLong } = _northEast;
  const { lat: southLat, lng: westLong } = _southWest;
  const _northWest = { lat: northLat, long: westLong };
  const _southEast = { lat: southLat, long: eastLong };
  return [_northWest, { lat: northLat, long: eastLong }, _southEast, { lat: southLat, long: westLong }, _northWest];
};

export const makeGeoPolygon: (latLongs) => GeoPolygon = latlongs => ({
  type: GeoPolygonType.geoPolygon,
  location: latlongs,
});

export const getPolygonLatLongs: (layer) => GeoPolygon = layer => {
  const { 0: latlongs } = layer.getLatLngs();
  const { 0: first } = latlongs;
  return makeGeoPolygon([...latlongs, first].map(({ lat, lng }) => ({ lat, long: lng })));
};

export const getCircleGeoPolygon: (layer) => GeoPolygon = layer => {
  const { lat, lng: long } = layer.getLatLng();
  const distance = layer.getRadius();
  return {
    type: GeoPolygonType.geoDistance,
    location: { lat, long } as any as LatLong,
    distance: `${Math.round(distance)}m`,
  };
};

export const makeShapePolygon = mapState => {
  const { shape, layer } = mapState.shape;
  return shape === LeafletShape.Polygon ? getPolygonLatLongs(layer) : getCircleGeoPolygon(layer);
};

export const makeSearchAsMoveControl = ({ onClick, value = false, localeText }) => {
  const checkBoxControl = L.Control.extend({
    options: {
      position: LeafletPosition.TopRight,
    },

    onAdd: () => {
      const checkboxId = 'markAsMoveCheckbox';
      const label = L.DomUtil.create(HtmlTagNames.Label, 'container');
      label.setAttribute(HtmlAttrsNames.For, checkboxId);
      const text = L.DomUtil.create(HtmlTagNames.Span, 'checkmark-label');
      text.innerText = localeText;
      const parentSpan = L.DomUtil.create(HtmlTagNames.Span, 'checkmark');
      parentSpan.appendChild(text);
      const checkbox = L.DomUtil.create(HtmlTagNames.Input, 'search-as-move-checkbox');
      checkbox.type = HtmlTagNames.Checkbox;
      checkbox.id = checkboxId;
      checkbox.value = String(value);
      checkbox.checked = value;
      checkbox.onchange = e => onClick(e);
      const wrapper = L.DomUtil.create(HtmlTagNames.Div, 'miq-search-as-move');
      label.appendChild(checkbox);
      label.appendChild(parentSpan);
      wrapper.appendChild(label);
      return wrapper;
    },
  });
  return new checkBoxControl();
};

export const makeInfoIconControl = translateText => {
  const infoBoxControl = L.Control.extend({
    options: { position: LeafletPosition.TopRight },

    onAdd: () => {
      const message = L.DomUtil.create(HtmlTagNames.Div, 'miq-warning-msg');
      message.setAttribute(HtmlAttrsNames.InnerHTML, translateText);

      const imgTag = L.DomUtil.create(HtmlTagNames.Img);
      imgTag.setAttribute(HtmlAttrsNames.Src, 'assets/svg/mapinfo-icon.svg');
      imgTag.setAttribute(HtmlAttrsNames.Alt, translateText);
      const wrapper = L.DomUtil.create(HtmlTagNames.Div, 'max-limit-reached');
      wrapper.onmouseenter = () => {
        message.className = 'miq-warning-msg miq-warning-msg-show';
      };
      wrapper.onmouseleave = () => {
        message.className = 'miq-warning-msg miq-warning-msg-hide';
      };
      wrapper.appendChild(message);
      wrapper.appendChild(imgTag);

      return wrapper;
    },
  });
  return new infoBoxControl();
};

export const makeEditIconControl = (translateText, onClick) => {
  const editBoxControl = L.Control.extend({
    options: {
      position: LeafletPosition.TopLeft,
    },

    onAdd: () => {
      const imgTag = L.DomUtil.create(HtmlTagNames.Img, 'edit-marker-icon');
      imgTag.setAttribute(HtmlAttrsNames.Src, 'assets/svg/edit-icon.svg');
      imgTag.setAttribute(HtmlAttrsNames.Alt, translateText);
      imgTag.onclick = e => onClick(e);
      const wrapper = L.DomUtil.create(HtmlTagNames.Div);
      wrapper.appendChild(imgTag);

      return wrapper;
    },
  });
  return new editBoxControl();
};

export const makeStreetViewControl = onClick => {
  const streetViewControl = L.Control.extend({
    options: {
      position: LeafletPosition.BottomRight,
    },

    onAdd: () => {
      const imgTag = L.DomUtil.create(HtmlTagNames.Img, 'street-view-icon');
      imgTag.setAttribute(HtmlAttrsNames.Src, 'assets/svg/street-view.svg');
      imgTag.onclick = onClick;
      const wrapper = L.DomUtil.create(HtmlTagNames.Div);
      wrapper.appendChild(imgTag);

      return wrapper;
    },
  });
  return new streetViewControl();
};

export const makeClearPolygonControl = (onClick, localeText) => {
  const checkBoxControl = L.Control.extend({
    options: {
      position: LeafletPosition.TopLeft,
    },

    onAdd: () => {
      const clearButton = L.DomUtil.create(HtmlTagNames.Button, 'clear-polygon-btn btn-light btn-cancel-outlined');
      clearButton.innerText = localeText;
      clearButton.onclick = e => onClick(e);
      return clearButton;
    },
  });
  return new checkBoxControl();
};

export const makeGroupingLayer = (markers: any[], options: any = {}) => {
  const clusterFn =
    (L as any).markerClusterGroup ||
    (globalThis as any)?.L?.markerClusterGroup ||
    (globalThis as any)?.window?.L?.markerClusterGroup;
  if (typeof clusterFn !== 'function') {
    // Keep map usable even when cluster plugin isn't loaded, but make it visible in console for QA.
    const group = L.featureGroup();
    markers.forEach(m => group.addLayer(m));
    return group;
  }
  const markersGroup = clusterFn(options);
  markersGroup.addLayers(markers);
  return markersGroup;
};

export const makeClickToAddPinControl = (onClick, localeText) => {
  const clickToAddPinControl = L.Control.extend({
    options: {
      position: LeafletPosition.TopRight,
    },

    onAdd: () => {
      const clickToAddButton = L.DomUtil.create(HtmlTagNames.Button, 'click-to-add-pin');
      clickToAddButton.innerText = localeText;
      clickToAddButton.onclick = e => onClick(e);
      return clickToAddButton;
    },
  });
  return new clickToAddPinControl();
};

/**
 * MAP AND BUILDS THE MARKERS
 *
 * L2-S12: the pin-drawing logic — which SVG, which colour, which size, which lat/lng, the
 * `esriMap` index bookkeeping — moved verbatim to `buildPinSpecs` in @miq/shared-models
 * (maps/pin-spec.ts), so the reports summary map can build the SAME pins without importing this
 * module (and with it leaflet-draw, markercluster and the card-info DOM helpers). What is left here
 * is the leaflet half that no @miq/* library may own: wrapping each spec in an `L.marker`. The
 * signature, the marker options and the `miqData` payload are unchanged, so every caller
 * (`makeMarkers`, map-template, map-widget, edit-marker-dialog) is untouched.
 */
export const buildMarkers = (
  markers,
  hoveredRow,
  isDraggable?: boolean,
  shadow?: boolean,
  esriMap?: { [markerId: string]: number },
  iconToUse?: MapIcon,
  clipboardMarkers?: number[]
) => {
  return buildPinSpecs(markers, hoveredRow, isDraggable, shadow, esriMap, iconToUse, clipboardMarkers).reduce(
    (acc, spec) => {
      const markerValue = L.marker([spec.lat, spec.lng], {
        icon: svgIconFactory(spec.svg, { width: spec.width, height: spec.height }),
        draggable: spec.draggable,
        pmIgnore: true,
      });
      (markerValue as any).miqData = spec.miqData;
      acc.push(markerValue);
      return acc;
    },
    []
  );
};

/**
 * SINCE COMPANY IS GIVING LOCATIONS AS NESTED, IT NEEDS SPECIAL TREATMENT
 * SO WE ADDED THIS FUNCTION (ONLY FOR COMPANY)
 */

const isValidLocation = ([, latitude, longitude]: [boolean, number, number]): boolean => {
  return latitude !== null && latitude !== 0 && longitude !== null && longitude !== 0;
};

const toCompanyMarkers = markers =>
  markers.map(([id, locations, pending]) => {
    const filterInvalidLocations = ((Array.isArray(locations) && locations) || []).filter(isValidLocation);
    return filterInvalidLocations.map(([isHeadQuarter, latitude, longitude]) => [
      id,
      latitude,
      longitude,
      pending,
      EMPTY_STRING,
      isHeadQuarter,
    ]);
  });

/** ALL OTHER ENTITIES THAN AVAILABILITY OR PROERTY SHOULD BE EMPTY MARKERS SO WE SKIP THE VALUE. */
const toEmptyMarkers = markers =>
  markers.map(([id, latitude, longitude, pending /** SKIP VALUE */, , isHeadQuarter]) => [
    id,
    latitude,
    longitude,
    pending,
    EMPTY_STRING,
    isHeadQuarter,
  ]);

/**
 * RETURN TRUE IF ENTITY IS NOT AVAILABILITY OR PROPERTY, SINCE THESE ARE THE
 * ONLY ENTITIES DISPLAYING A VALUE, ALL OTHERS WOULD BE EMPTY
 */
const shouldBeEmptyMarker = entity => ![EntityType.Availability, EntityType.Property].some(x => x === entity);

/**
 * ONLY PROPERTY AND AVAILABILITY BUBBLES WILL HAVE VALUE INSIDE,
 * ALL OTHERS SHOULD FALL POSITIVE into shouldBeEmptyMarker
 */
export const makeMarkers = (
  { markers, hoveredRow, showOnlyPending = false, entity, clipboardMarkers },
  esriMap?: { [markerId: string]: number }
) => {
  markers = Array.isArray(markers) ? markers : [];
  markers = showOnlyPending ? markers.filter(({ 3: pending }) => pending) : markers;
  if (shouldBeEmptyMarker(entity)) {
    // APPLY SPECIAL TREATMENT FOR COMPANY & CONTACT, ALL OTHERS (SALE, LEASE, SHOULD BE EMPTY ONLY)
    markers =
      entity === EntityType.Company || entity === EntityType.Contact
        ? toCompanyMarkers(markers).flat(1)
        : toEmptyMarkers(markers);
  }
  // To protect performance limit 5000 OR less (total)
  markers = markers.slice(0, 5000);
  return buildMarkers(markers, hoveredRow, false, false, esriMap, null, clipboardMarkers);
};

export const updateMarkers = (
  markers,
  hoveredRow: { id: number; previous?: number },
  esriMap?: { [markerId: string]: number },
  clipboardMarkers?: number[]
) => {
  const isMouseout = hoveredRow.previous != null;
  const id = isMouseout ? hoveredRow.previous : hoveredRow.id;
  return markers.map((marker, index) => {
    const { value, pending } = (marker as any).miqData ?? marker.options;
    const { lat, lng } = marker._latlng;
    const markerId = `${id}.${lat}.${lng}`;
    if (!!esriMap) {
      if (esriMap[markerId] != null && esriMap[markerId] === index) {
        setMarkerIcon(
          marker,
          value,
          pending,
          !isMouseout,
          clipboardMarkers && clipboardMarkers.indexOf(id) !== -1 ? MapIcon.ClipboardMapIcon : null
        );
      }
    } else {
      setMarkerIcon(
        marker,
        value,
        pending,
        ((marker as any).miqData ?? marker.options).id === hoveredRow.id,
        clipboardMarkers && clipboardMarkers?.indexOf(id) !== -1 ? MapIcon.ClipboardMapIcon : null
      );
    }
    return marker;
  });
};

function setMarkerIcon(marker: any, value: any, pending: any, hoveredCondition: boolean, iconToUse) {
  const iconOpts = {
    text: !!+value ? abbrevNumber(value) : EMPTY_STRING,
    pending,
    hovered: hoveredCondition,
    iconToUse,
  };
  const icon = svgIconMaker(iconOpts);
  marker.setIcon(icon);
}

export function formattedReadableDistance() {
  const readableDistance = L.GeometryUtil.readableDistance;
  L.GeometryUtil.readableDistance = (distance, isMetric, isFeet) => {
    if (isMetric || !isFeet) {
      return readableDistance(distance, isMetric, isFeet);
    }

    // convert to feet/miles, start using miles at quarter mile
    distance *= MetricConversion.footInAMeter;
    if (distance > MetricConversion.footInAMile / 4) {
      return L.GeometryUtil.formattedNumber(distance / MetricConversion.footInAMile, 2) + ' miles';
    }
    return L.GeometryUtil.formattedNumber(distance, 0) + ' ft';
  };
}

// L2-S12: moved verbatim to @miq/shared-models (maps/pin-spec.ts) with its one caller,
// `buildPinSpecs`. Re-exported from the old path so the move is discoverable from here.
export { getPaddingValue } from '@miq/shared-models';
export function getOffsetToCenter(zoom: number): number {
  let offsetToCenter = 1;
  if (zoom >= 17) {
    offsetToCenter = 0.0005;
  } else if (zoom >= 14) {
    offsetToCenter = 0.001;
  } else if (zoom >= 12) {
    offsetToCenter = 0.005;
  } else if (zoom >= 9) {
    offsetToCenter = 0.01;
  } else if (zoom >= 6) {
    offsetToCenter = 0.1;
  }
  return offsetToCenter;
}

export const makeLatLngBounds = ({ _northEast, _southWest }) => {
  return new L.LatLngBounds(_northEast, _southWest);
};

export const leafletifyShape = ({ shape, layer }) => {
  layer =
    shape === LeafletShape.Polygon
      ? L.polygon(layer._latlngs, layer.options)
      : L.circle(layer._latlng, { radius: layer._mRadius, ...layer.options });
  return { shape, layer };
};

export const groupMarkerIconMaker = cluster => {
  const pendingMarker = cluster
    .getAllChildMarkers()
    .map(m => (m as any).miqData ?? m.options)
    .find(({ pending }) => !!pending);
  const pendingTag = !!pendingMarker ? `<span class="pending-group"></span>` : EMPTY_STRING;
  return L.divIcon({
    html: `<div><span>${cluster.getChildCount()}</span> ${pendingTag} </div>`,
    className: 'marker-cluster marker-cluster-medium',
    iconSize: new L.Point(40, 40),
  });
};

/**
 * L2-S6: `isGaodeGeocode` and `isGaodeMap` MOVED to @miq/shared-models
 * (maps/gaode.helpers.ts) — they were leaf-clean (digger + `LOCALE_MAP_CLIENT_MAP` /
 * `LocaleMapId` / `MapClientKey` / `DataObject`, all already in that library, zero leaflet), and
 * they were two of the three symbols the reports remote imported from this 539-line,
 * leaflet-plugin-bootstrapping module. This re-export keeps the old path working for
 * `visual-widget/map-widget/map-widget.component` and for the internal callers below
 * (`multipleLocations`, `singleLocation`); new code should import from '@miq/shared-models'.
 */
export { isGaodeGeocode, isGaodeMap } from '@miq/shared-models';

export const getGaodeIndex = (userLocaleId: LocaleMapId, data, path: string) => {
  // considering gaode geo block exists in 'postal_address.addl_geocodes' array
  const addlGeocodes = digger(data, path);
  return addlGeocodes.findIndex(
    geocodes => geocodes.geocode_source_provider_type === LOCALE_MAP_CLIENT_MAP[userLocaleId].toUpperCase()
  );
};

export const multipleLocations = (location, index: number, postalAddress: string, userLocaleId: LocaleMapId) => {
  let latitude;
  let longitude;
  const gaodeGeocodes = isGaodeGeocode(userLocaleId, location, `${postalAddress}.addl_geocodes`);
  if (gaodeGeocodes) {
    latitude = gaodeGeocodes.latitude;
    longitude = gaodeGeocodes.longitude;
  } else {
    latitude = digger(location, `${postalAddress}`).latitude;
    longitude = digger(location, `${postalAddress}`).longitude;
  }
  return [index + 1, latitude, longitude, false, EMPTY_STRING, location['headquarter_location_f']];
};

export const getMapLocations = (location: DataObject, postalAddressPath: string) => {
  const addlGeocodes: DataObject[] = digger(location, `${postalAddressPath}.addl_geocodes`);
  return {
    gaodeLocation: addlGeocodes?.length ? getGaodeLocation(location, addlGeocodes[0]) : null,
    defaultLocation: getDefaultLocation(location, postalAddressPath),
  };
};

export const getGaodeLocation = (location: DataObject, gaodeGeocodes: DataObject) => {
  return [[digger(location, 'entity.id'), gaodeGeocodes.latitude, gaodeGeocodes.longitude, false, EMPTY_STRING, false]];
};

export const getDefaultLocation = (location: DataObject, postalAddressPath: string) => {
  return [
    [
      digger(location, 'entity.id'),
      digger(location, `${postalAddressPath}.latitude`),
      digger(location, `${postalAddressPath}.longitude`),
      false,
      EMPTY_STRING,
      false,
    ],
  ];
};
export const singleLocation = (location: DataObject, postalAddressPath: string, userLocaleId: LocaleMapId) => {
  const gaodeGeocodes = isGaodeGeocode(userLocaleId, location, `${postalAddressPath}.addl_geocodes`);
  if (gaodeGeocodes) {
    return getGaodeLocation(location, gaodeGeocodes);
  }
  return getDefaultLocation(location, postalAddressPath);
};

/**
 *
 * @param colloquial
 * @param countryCodeDesc
 * @returns colloquial in case the country code coincides with that of the user
 */
const findCountryColloquial = (colloquial: DataObject[], countryCodeDesc: string): DataObject | null => {
  return colloquial
    .filter(({ country_code_desc }) => country_code_desc != null)
    .find(({ country_code_desc }) => countryCodeDesc === country_code_desc);
};

/**
 *
 * @param data
 * @param colloquial
 * @param countryCodeDesc
 * @param param
 * @returns colloquial or record search parameter value
 */
export function getColloquialParam(
  data: DataObject,
  colloquial: DataObject[],
  countryCodeDesc: string,
  param: string
): string | null {
  const countryColloquial = findCountryColloquial(colloquial, countryCodeDesc);
  return countryColloquial ? countryColloquial[param] : data[param];
}

// Configuration for colloquial search in case it does not come in the record
export const EntityInfoColloquial: EntityInfoColloquialModel = {
  [EntityType.Contact]: {
    entity: EntityType.Company,
    getId: (record: DataObject) => {
      const companies = digger(record, 'company_role_addresses');
      const company = companies.length > 0 ? companies[0] : null;
      return company?.company_id;
    },
  },
  [EntityType.Tenancy]: {
    entity: EntityType.Property,
    getId: (record: DataObject) => {
      return record.property_id;
    },
  },
};
